import React, { useState } from 'react';
import { IHTCalculation, EstateProfile } from '../types';
import { Calculator, AlertCircle, CheckCircle2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { MISSING_INFO_LABELS } from './probateCopyMaps';
import { HelpPopover } from './HelpPopover';
import { HELP_CONTENT } from './helpContent';
import { emitProbateScroll } from './probate/scrollTargets';
import { RegulationReference } from './results/RegulationReference';
import { selectReportPanels } from './results/selectReportPanels';
import { TRUST_ESTATE_PROVISIONAL_SHORT, TRUST_ESTATE_ZERO_TAX } from './results/trustEstateProvisional';
import { getAuthority } from '../server/rules/authorities';

interface IHTCalculatorPanelProps {
 profile: EstateProfile;
 ihtCalc?: IHTCalculation | null;
 onChange: (newProfile: EstateProfile) => void;
 availableProperties: Array<{ id: string; address?: string; estimated_value?: number }>;
 goToStep?: (stepId: string) => boolean;
 mode?: 'builder' | 'results'; // [NEW] Mode prop
}

export const IHTCalculatorPanel: React.FC<IHTCalculatorPanelProps> = ({ profile, ihtCalc, onChange, availableProperties, goToStep, mode = 'builder' }) => {
 // In results mode: default expanded if tax is due, collapsed if no tax
 const hasTaxDue = (ihtCalc?.total_tax_due ?? 0) > 0;
 const [breakdownOpen, setBreakdownOpen] = useState(mode !== 'results' || hasTaxDue);
 const [auditOpen, setAuditOpen] = useState(false);

 // Single source of truth for which panels mount (banner / figures). Keyed on the
 // date-gate blockers: pre-IHT suppresses figures; pre-2017 is indicative+banner.
 const panels = selectReportPanels(ihtCalc);
 // WP1: trust property that may belong in the estate is not included (engine gap) — no zero-tax conclusion, figures provisional.
 const trustGap = !!ihtCalc?.breakdown?.trust_estate_inclusion_gap;

 // Helper to update deeply nested state
 const updateIHT = (path: string[], value: any) => {
 // ... (existing update logic) ...
 const newProfile = { ...profile };
 if (!newProfile.iht_details) newProfile.iht_details = {};

 let current: any = newProfile.iht_details;
 for (let i = 0; i < path.length - 1; i++) {
 if (!current[path[i]]) current[path[i]] = {};
 current = current[path[i]];
 }
 current[path[path.length - 1]] = value;
 onChange(newProfile);
 };

 const details = profile.iht_details || {};
 const spouse = details.predeceased_spouse || {};
 const residence = details.residence_bands || {};
 const hasLateSpouse = spouse.unused_nrb_percent !== 0 || spouse.unused_rnrb_percent !== 0;

 // ... (existing handleFix logic) ...
 const handleFix = (b: string) => {
 // ... (keep existing handleFix implementation) ...
 const anchorExists = !!document.getElementById("estatebuilder-properties");
 console.debug("[probate] CTA click: handleFix", { blocker: b, route: window.location.pathname, anchorExists });

 if (b === 'MISSING_NET_VALUE' || b === 'MISSING_NET_FOR_TAPER') {
 document.getElementById('debts-section')?.scrollIntoView({ behavior: 'smooth' });
 }

 // Property Fixes -> Emit Scroll Event or Navigate
 else if (b === 'MISSING_MAIN_RESIDENCE_SELECTION' || b === 'MULTIPLE_MAIN_RESIDENCES' ||
 b === 'MISSING_DECEASED_SHARE_PERCENT' || b === 'MISSING_HOME_INHERITANCE_DESTINATION' ||
 b === 'MISSING_QUALIFYING_PROPERTY_VALUE' || b === 'MISSING_MAIN_RESIDENCE_VALUE' ||
 b === 'MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE' || b === 'MISSING_ASSET_INFO') {

 // Define focus target
 let focus:"main_residence" |"deceased_share" |"passes_to" | undefined = undefined;
 if (b === 'MISSING_MAIN_RESIDENCE_SELECTION' || b === 'MULTIPLE_MAIN_RESIDENCES' || b === 'MISSING_QUALIFYING_PROPERTY_VALUE' || b === 'MISSING_MAIN_RESIDENCE_VALUE') focus = 'main_residence';
 else if (b === 'MISSING_DECEASED_SHARE_PERCENT') focus = 'deceased_share';
 else if (b === 'MISSING_HOME_INHERITANCE_DESTINATION' || b === 'MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE') focus = 'passes_to';
 else if (b === 'MISSING_ASSET_INFO') focus = 'main_residence';

 // STRATEGY: Check if we can just scroll directly first (fastest)
 const el = document.getElementById("estatebuilder-properties");
 if (el) {
 console.debug("[probate] Found anchor, scrolling immediately", b);
 el.scrollIntoView({ behavior: 'smooth', block: 'center' });
 // Also emit event to trigger internal focus logic in EstateBuilder
 emitProbateScroll({ target: 'properties', focus });
 return;
 }

 // If anchor not found, WE MUST NAVIGATE.
 console.debug("[probate] Anchor not found, attempting navigation", b);

 // 1. Try 'property' step (if available) -> This is cleaner if the step exists
 if (goToStep && goToStep('property')) {
 setTimeout(() => emitProbateScroll({ target: 'properties', focus }), 500);
 return;
 }

 // 2. Try 'review' step (where EstateBuilder usually lives)
 if (goToStep) {
 // Force nav to review
 goToStep('review');
 // Wait for mount
 setTimeout(() => emitProbateScroll({ target: 'properties', focus }), 500);
 }
 }
 };

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className={`border-b border-[#E8E4DB] px-4 py-3 flex justify-between items-center ${mode === 'results' ? 'bg-white' : 'bg-[#FAFAF8]'}`}>
 <h3 className="text-sm font-bold text-[#1A1A1A] flex items-center gap-2">
 <Calculator className="w-4 h-4 text-[#3730A3]" />
 {mode === 'results' ?"Estimated Inheritance Tax" :"Inheritance Tax Calculator"}
 </h3>
 </div>

 <div className="p-5 space-y-6">

 {/* Date-of-death outside the supported range — figures are indicative only. */}
 {panels.showBanner && (
 <div data-testid="unsupported-date-banner" className="bg-[#FAF3E0] border border-[#E8D89A] rounded p-3 text-left">
 <h4 className="text-xs font-bold text-[#5A4200] uppercase mb-1 flex items-center gap-1">
 <AlertCircle className="w-3 h-3" /> Date of death outside supported range
 </h4>
 <p className="text-[11px] text-[#5A4200] leading-relaxed">
 {panels.bannerKind === 'pre_iht'
 ? 'This estate has a date of death before 18 March 1986, when Inheritance Tax did not yet exist — Capital Transfer Tax or Estate Duty applied instead. This tool cannot compute that regime, so no tax figures are shown. Please seek professional advice.'
 : 'This estate has a date of death before 6 April 2017. The figures and HMRC references shown are indicative only — the residence nil rate band and some current rules did not yet apply on that date. Please seek professional advice before relying on this estimate.'}
 </p>
 </div>
 )}

 {/* Builder Mode: Action Required Panel (In Results mode, this is handled by ActionablePending) */}
 {mode === 'builder' && ihtCalc?.status === 'pending' && ihtCalc.blockers.length > 0 && (
 <div className="bg-[#FAF3E0] border border-[#E8D89A] rounded p-3 text-left animate-in slide-in-from-top-2">
 {/* ... existing blocker UI ... */}
 <h4 className="text-xs font-bold text-[#5A4200] uppercase mb-2 flex items-center gap-1">
 <AlertCircle className="w-3 h-3" /> A few details to confirm
 </h4>
 <div className="space-y-2">
 {ihtCalc.blockers.map(b => (
 <div key={b} className="flex justify-between items-center bg-white p-2 rounded border border-[#E8D89A]">
 <span className="text-xs text-[#3F3D39] font-medium">
 {MISSING_INFO_LABELS[b]?.title || b}
 </span>
 <button
 onClick={() => handleFix(b)}
 className="text-[10px] font-bold text-[#3730A3] hover:text-[#3730A3] hover:underline px-2 py-1 bg-[#EEF0FA] rounded border border-[#C7CBEA]"
 >
 To continue
 </button>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Builder Mode: Inputs */}
 {mode === 'builder' && (
 <>
 {/* 1. Transferable Allowance */}
 <div className="space-y-4">
 <div className="flex items-center gap-2 border-b border-[#E8E4DB] pb-2">
 <h4 className="text-xs font-bold uppercase text-[#6B6762]">1. Transferable Allowance</h4>
 <HelpPopover content={HELP_CONTENT.TRANSFERABLE_ALLOWANCE} />
 </div>

 <div className="bg-[#FAFAF8] p-3 rounded-lg border border-[#E8E4DB]">
 <p className="text-sm font-medium text-[#3F3D39] mb-3">
 Was the deceased widowed (did they have a spouse or civil partner who died before them)?
 </p>

 <div className="flex gap-4 mb-2">
 <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded border border-[#E8E4DB] hover:border-[#C7CBEA] transition-colors">
 <input
 type="radio"
 name="has_late_spouse"
 className="text-[#3730A3] focus:ring-[#3730A3]"
 checked={spouse.exists === true}
 onChange={() => {
 updateIHT(['predeceased_spouse', 'exists'], true);
 }}
 />
 <span className="text-sm">Yes</span>
 </label>
 <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded border border-[#E8E4DB] hover:border-[#C7CBEA] transition-colors">
 <input
 type="radio"
 name="has_late_spouse"
 className="text-[#3730A3] focus:ring-[#3730A3]"
 checked={spouse.exists === false}
 onChange={() => {
 updateIHT(['predeceased_spouse', 'exists'], false);
 // Auto-clear inputs
 updateIHT(['predeceased_spouse', 'unused_nrb_percent'], null);
 updateIHT(['predeceased_spouse', 'unused_rnrb_percent'], null);
 }}
 />
 <span className="text-sm">No</span>
 </label>
 </div>

 {spouse.exists === true && (
 <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E8E4DB] animate-in fade-in slide-in-from-top-1">
 <div>
 <label className="flex items-center gap-1 text-[10px] font-bold text-[#6B6762] mb-1">
 Unused basic allowance (NRB) (%)
 <HelpPopover content={HELP_CONTENT.UNUSED_NRB_PERCENT || { what_it_means:"Percentage of the £325k allowance unused by the first spouse.", why_we_ask:"This increases the tax-free amount for this estate.", examples: ["100% = They used none","0% = They used it all"] }} />
 </label>
 <div className="relative">
 <input
 type="number"
 min="0" max="100"
 className="w-full text-sm border rounded p-2 pr-8 focus:ring-2 focus:ring-[#C7CBEA]"
 placeholder="0 - 100"
 value={spouse.unused_nrb_percent ?? ''}
 onChange={(e) => updateIHT(['predeceased_spouse', 'unused_nrb_percent'], Number(e.target.value))}
 />
 <span className="absolute right-3 top-2 text-[#AFA99F] text-sm">%</span>
 </div>
 </div>
 <div>
 <label className="flex items-center gap-1 text-[10px] font-bold text-[#6B6762] mb-1">
 Unused home allowance (RNRB) (%)
 <HelpPopover content={HELP_CONTENT.UNUSED_RNRB_PERCENT || { what_it_means:"Percentage of the £175k home allowance unused by the first spouse.", why_we_ask:"This increases the home allowance for this estate.", examples: ["100% = They didn't use it"] }} />
 </label>
 <div className="relative">
 <input
 type="number"
 min="0" max="100"
 className="w-full text-sm border rounded p-2 pr-8 focus:ring-2 focus:ring-[#C7CBEA]"
 placeholder="0 - 100"
 value={spouse.unused_rnrb_percent ?? ''}
 onChange={(e) => updateIHT(['predeceased_spouse', 'unused_rnrb_percent'], e.target.value === '' ? null : Number(e.target.value))}
 />
 <span className="absolute right-3 top-2 text-[#AFA99F] text-sm">%</span>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>

 <div className="border-t border-[#E8E4DB]"></div>

 {/* 2. Residence Nil Rate Band (Status Block) */}
 <div className="space-y-3">
 <div className="flex items-center gap-2">
 <h4 className="text-xs font-bold uppercase text-[#6B6762]">2. Residence Nil Rate Band (RNRB)</h4>
 <HelpPopover content={HELP_CONTENT.RNRB} />
 </div>

 <div className="bg-white border border-[#E8E4DB] rounded-lg p-4 space-y-4">
 <p className="text-xs text-[#3F3D39] leading-relaxed">
 We calculate the Residence Nil Rate Band automatically using:
 </p>
 <ul className="list-disc pl-4 text-xs text-[#6B6762] space-y-1">
 <li>which property is marked as the main home</li>
 <li>the deceased’s ownership share</li>
 <li>who inherits that share</li>
 </ul>

 {/* Status Check */}
 {(() => {
 const rnrbBlockers = [
 'MISSING_MAIN_RESIDENCE_SELECTION', 'MULTIPLE_MAIN_RESIDENCES',
 'MISSING_DECEASED_SHARE_PERCENT', 'MISSING_HOME_INHERITANCE_DESTINATION',
 'MISSING_QUALIFYING_PROPERTY_VALUE', 'MISSING_MAIN_RESIDENCE_VALUE',
 'MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE', 'MISSING_ASSET_INFO'
 ];
 const hasIssue = ihtCalc?.blockers.some(b => rnrbBlockers.includes(b));

 if (hasIssue) {
 return (
 <div className="bg-[#FAF3E0] border border-[#E8D89A] rounded p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
 <div className="flex items-center gap-2">
 <AlertCircle className="w-4 h-4 text-[#7A5A00]" />
 <span className="text-xs font-bold text-[#5A4200]">Status: Main home details required</span>
 </div>
 <button
 onClick={() => handleFix('MISSING_MAIN_RESIDENCE_SELECTION')}
 className="text-xs bg-white border border-[#E8D89A] text-[#7A5A00] font-bold px-3 py-1.5 rounded hover:bg-[#FAF3E0] transition-colors whitespace-nowrap"
 >
 Go to Properties
 </button>
 </div>
 );
 }

 return (
 <div className="bg-white border border-[#E8E4DB] rounded p-3 flex items-center gap-2">
 <CheckCircle2 className="w-4 h-4 text-[#3730A3]" />
 <div>
 <div className="text-xs font-bold text-[#1A1A1A]">Status: Main home details confirmed</div>
 <div className="text-[10px] text-[#3730A3]">RNRB will be calculated automatically.</div>
 </div>
 </div>
 );
 })()}
 </div>
 </div>
 </>
 )}

 {/* 3. Results Breakdown — suppressed entirely for pre-IHT dates (panels.showFigures) */}
 {ihtCalc && panels.showFigures && (
 <div data-testid="estate-tax-figures" className={`rounded-lg border ${mode === 'results' ? 'bg-white border-[#E8E4DB]' : 'bg-[#FAFAF8] border-[#E8E4DB] p-4'} mt-4`}>

 {/* Summary Block (Always Visible) */}
 <div className="flex justify-between items-end mb-4">
 <div>
 <h4 className="text-sm font-bold text-[#1A1A1A]">Estimated IHT Bill</h4>
 {ihtCalc.breakdown.net_value != null && (
 <p className="text-xs text-[#6B6762]">Based on an estimated net estate of {ihtCalc.breakdown.net_value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}</p>
 )}
 {/* Calculation insight for tax-due cases */}
 {(ihtCalc.total_tax_due ?? 0) > 0 && (() => {
 const taxableAmt = ihtCalc.breakdown.taxable_value;
 if (taxableAmt != null && taxableAmt > 0) {
 return (
 <p className="text-[10px] text-[#6B6762] mt-0.5">
 Estimated because the estate value exceeds the available tax-free allowances by approximately {taxableAmt.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}.
 </p>
 );
 }
 return (
 <p className="text-[10px] text-[#6B6762] mt-0.5">
 Estimated because the estate value exceeds the available tax-free allowances.
 </p>
 );
 })()}
 </div>
 <div className="text-right">
 {ihtCalc.total_tax_due === 0 ? (
 <span className="block text-xl font-black text-[#3730A3]">{trustGap ? 'None calculated (provisional)' : 'No tax expected'}</span>
 ) : (
 <span className="block text-2xl font-black text-[#1A1A1A]">
 {ihtCalc.total_tax_due?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) ?? '-'}
 </span>
 )}
 {trustGap && (
 <span data-testid="calc-panel-trust-provisional" className="block text-xs text-[#7A5A00] font-medium">{TRUST_ESTATE_PROVISIONAL_SHORT}</span>
 )}
 {ihtCalc.status === 'pending' && (
 <span className="text-xs text-[#7A5A00] font-medium">Calculation pending inputs</span>
 )}
 </div>
 </div>

 {/* Zero-tax structured explanation */}
 {ihtCalc.total_tax_due === 0 && ihtCalc.breakdown.zero_tax_detail && (() => {
 const zt = ihtCalc.breakdown.zero_tax_detail;
 const fmtZ = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 return (
 <div className="mb-4 bg-white border border-[#E8E4DB] rounded-lg p-4 space-y-3">
 <p className="text-sm font-bold text-[#1A1A1A]">{trustGap ? 'Why no inheritance tax is calculated on the figures included' : 'Why no Inheritance Tax is due'}</p>
 {trustGap && (
 <p className="text-xs text-[#7A5A00] leading-relaxed">
 {TRUST_ESTATE_ZERO_TAX} Trust property that may belong in the estate has not been included, and including it could change the liability.
 </p>
 )}
 <p className="text-xs text-[#1A1A1A] leading-relaxed">{trustGap ? `On the figures currently included: ${zt.summary}` : zt.summary}</p>

 {/* Deductions that reduced the estate */}
 {zt.deductions.length > 0 && (
 <div>
 <p className="text-[10px] font-bold text-[#3730A3] uppercase tracking-wide mb-1">Deductions applied</p>
 {zt.deductions.map((d, i) => (
 <div key={i} className="flex justify-between text-xs text-[#1A1A1A] py-0.5">
 <span>{d.label}</span>
 <span className="font-mono">{fmtZ(d.amount)}</span>
 </div>
 ))}
 </div>
 )}

 {/* Allowances covering the remainder */}
 {zt.allowances.length > 0 && (
 <div>
 <p className="text-[10px] font-bold text-[#3730A3] uppercase tracking-wide mb-1">Tax-free allowances</p>
 {zt.allowances.map((a, i) => (
 <div key={i} className="flex justify-between text-xs text-[#1A1A1A] py-0.5">
 <span>{a.label}</span>
 <span className="font-mono">{fmtZ(a.amount)}</span>
 </div>
 ))}
 <div className="flex justify-between text-xs font-bold text-[#1A1A1A] pt-1 border-t border-[#E8E4DB] mt-1">
 <span>Total allowances</span>
 <span className="font-mono">{fmtZ(zt.total_allowances)}</span>
 </div>
 </div>
 )}

 {/* Headroom */}
 {zt.headroom > 0 && (
 <p className="text-[10px] text-[#3730A3] italic">
 {trustGap
 ? <>Unused allowance headroom on the figures currently included: {fmtZ(zt.headroom)} (chargeable estate {fmtZ(zt.non_exempt_estate)}). Trust property not yet included could use some or all of it.</>
 : <>Unused allowance headroom: {fmtZ(zt.headroom)} — the chargeable estate ({fmtZ(zt.non_exempt_estate)}) is comfortably within the available allowances.</>}
 </p>
 )}

 {/* RNRB eligibility checklist */}
 {zt.rnrb_eligibility && zt.rnrb_eligibility.length > 0 && (
 <div className="pt-2 border-t border-[#E8E4DB]">
 <p className="text-[10px] font-bold text-[#3730A3] uppercase tracking-wide mb-1">RNRB eligibility</p>
 {zt.rnrb_eligibility.map((r, i) => (
 <div key={i} className="flex items-center gap-1.5 text-[11px] text-[#1A1A1A] py-0.5">
 {r.met
 ? <CheckCircle2 className="w-3 h-3 text-[#3730A3] shrink-0" />
 : <AlertCircle className="w-3 h-3 text-[#7A5A00] shrink-0" />}
 <span className={r.met ? '' : 'text-[#7A5A00]'}>{r.label}</span>
 </div>
 ))}
 </div>
 )}

 {/* Spouse exemption provenance */}
 {ihtCalc.breakdown.spouse_exemption_source && (
 <p className="text-[10px] text-[#3730A3] italic pt-1 border-t border-[#E8E4DB]">
 Spouse exemption: {ihtCalc.breakdown.spouse_exemption_source}
 </p>
 )}

 {/* Gift no-tax validation */}
 {ihtCalc.breakdown.gift_no_tax_note && (
 <p className="text-[10px] text-[#3730A3] italic">
 {ihtCalc.breakdown.gift_no_tax_note}
 </p>
 )}
 </div>
 );
 })()}
 {/* Fallback for zero-tax without structured detail */}
 {ihtCalc.total_tax_due === 0 && !ihtCalc.breakdown.zero_tax_detail && ihtCalc.breakdown.total_allowance != null && ihtCalc.breakdown.non_exempt_total != null && (
 <div className="mb-4 text-xs text-[#1A1A1A] bg-white border border-[#E8E4DB] p-2 rounded">
 <strong>{trustGap ? 'Key reason no tax is calculated on the figures included (provisional):' : 'Key reason no tax is expected:'}</strong> available allowances ({ihtCalc.breakdown.total_allowance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}) exceed the chargeable estate ({ihtCalc.breakdown.non_exempt_total.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}).
 </div>
 )}

 {/* Tax-due payment note */}
 {mode === 'results' && ihtCalc.total_tax_due != null && ihtCalc.total_tax_due > 0 && (
 <div className="mb-4 text-xs text-[#3F3D39] bg-[#FAF3E0] border border-[#E8D89A] p-2 rounded">
 <strong>Note:</strong> IHT is usually payable before probate is granted — interest accrues after the 6-month due date.
 </div>
 )}

 {/* Dynamic instalment guidance — only when relevant */}
 {mode === 'results' && ihtCalc.total_tax_due != null && ihtCalc.total_tax_due > 0 && (() => {
 // Check if qualifying property or land assets exist
 const hasQualifyingProperty = availableProperties.some(p => (p.estimated_value ?? 0) > 0);
 const hasBPR = (ihtCalc.breakdown.bpr_relief_total ?? 0) > 0;
 if (!hasQualifyingProperty && !hasBPR) return null;
 return (
 <div className="mb-4 text-xs text-[#3730A3] bg-[#EEF0FA] border border-[#C7CBEA] p-2 rounded">
 <strong>Instalment option:</strong> Inheritance Tax on certain property-related assets may be payable by 10 annual instalments, with interest. Review whether instalment payment may apply to this estate.
 </div>
 );
 })()}

 {/* RNRB taper message */}
 {mode === 'results' && (() => {
 const taperAmt = ihtCalc.breakdown.taper_amount ?? 0;
 const rnrbBefore = ihtCalc.breakdown.rnrb_before_taper ?? 0;
 const rnrbAfter = ihtCalc.breakdown.rnrb_available ?? 0;
 // The taper reduces the allowance, not the residence value (PA-IHT-RNRB-TAPER-CALC-001):
 // only message when the RNRB itself was reduced.
 if (taperAmt <= 0 || rnrbAfter >= rnrbBefore) return null;
 return (
 <div className="mb-4 text-xs text-[#7A5A00] bg-[#FAF3E0] border border-[#E8D89A] p-2 rounded">
 {rnrbAfter === 0
 ? <><strong>RNRB taper:</strong> No Residence Nil Rate Band is available because the estate exceeds the taper threshold (£2,000,000). The full RNRB entitlement has been removed.</>
 : <><strong>RNRB taper:</strong> Part of the Residence Nil Rate Band has been lost because the estate exceeds the £2 million taper threshold. The Residence Nil Rate Band is reduced for estates above £2 million.</>
 }
 </div>
 );
 })()}

 {/* Collapsible toggle */}
 {mode === 'results' && (
 <button
 onClick={() => setBreakdownOpen(!breakdownOpen)}
 className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide my-3 ${
 !breakdownOpen && (ihtCalc.total_tax_due ?? 0) > 0
 ? 'text-[#3730A3] hover:text-[#3730A3]'
 : 'text-[#6B6762] hover:text-[#1A1A1A]'
 }`}
 >
 {breakdownOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
 {breakdownOpen ?"Hide full calculation" :"Show full calculation"}
 </button>
 )}

 {/* Calculation chain */}
 <div className={`space-y-0 text-xs ${mode === 'results' && !breakdownOpen ? 'hidden' : ''}`}>
 {/* Issue 7: Section label */}
 <p className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest mb-2">Calculation summary</p>
 {(() => {
 const b = ihtCalc.breakdown;
 const fmt = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 const fmtSigned = (n: number) => n === 0 ? '—' : `-${fmt(n)}`;

 const rows: { label: string; value: string; indent?: boolean; bold?: boolean; separator?: boolean; positive?: boolean; note?: string }[] = [];

 // Issue 1: Always derive net estate = gross - debts
 const grossValue = b.gross_value ?? 0;
 const debtsTotal = b.debts_total ?? 0;
 const derivedNetEstate = grossValue - debtsTotal;

 if (b.gross_value != null) rows.push({ label: 'Estate before debts', value: fmt(b.gross_value) });
 if (debtsTotal > 0) rows.push({ label: 'Debts and liabilities', value: fmtSigned(debtsTotal), indent: true });
 if (b.gross_value != null) rows.push({ label: 'Net estate after debts', value: fmt(derivedNetEstate), bold: true, separator: true });

 if ((b.bpr_relief_total ?? 0) > 0) rows.push({ label: 'Business Property Relief (BPR) applied to qualifying assets', value: fmtSigned(b.bpr_relief_total!), indent: true, positive: true });
 if ((b.apr_relief_total ?? 0) > 0) rows.push({ label: 'Agricultural Property Relief (APR) applied to qualifying assets', value: fmtSigned(b.apr_relief_total!), indent: true, positive: true });
 if ((b.spouse_exemption_total ?? 0) > 0) rows.push({ label: 'Less: Spouse / civil partner exemption', value: fmtSigned(b.spouse_exemption_total!), indent: true, positive: true, note: b.spouse_exemption_source ?? undefined });
 if ((b.charity_exemption_total ?? 0) > 0) rows.push({ label: 'Less: Charitable exemption', value: fmtSigned(b.charity_exemption_total!), indent: true, positive: true });
 {
 const deductions = (b.bpr_relief_total ?? 0) + (b.apr_relief_total ?? 0) + (b.spouse_exemption_total ?? 0) + (b.charity_exemption_total ?? 0);
 const computed = derivedNetEstate - deductions;
 const chargeableValue = (b.non_exempt_total != null && Math.abs(b.non_exempt_total - computed) < 1)
 ? b.non_exempt_total
 : Math.max(0, computed);
 rows.push({ label: 'Chargeable estate before allowances', value: fmt(chargeableValue), bold: true, separator: true });
 }

 // NRB — show how gifts consumed it
 const nrbAvailable = b.nrb_available ?? 0;
 const rnrbAvailable = b.rnrb_available ?? 0;
 if (b.nrb_available != null) {
 const giftsUsed = b.gifts_chargeable_total ?? 0;
 const nrbBase = b.nrb_available + giftsUsed;
 if (giftsUsed > 0) {
 rows.push({ label: 'Nil Rate Band (base)', value: fmt(nrbBase) });
 rows.push({ label: 'Less: Used by lifetime gifts', value: fmtSigned(giftsUsed), indent: true, note: 'Chargeable gifts within 7 years reduce the NRB available against the estate.' });
 rows.push({ label: 'Available NRB', value: fmt(b.nrb_available), indent: true, bold: true });
 } else {
 rows.push({ label: 'Nil Rate Band', value: fmt(b.nrb_available) });
 }
 }
 if (rnrbAvailable > 0 || (b.rnrb_before_taper ?? 0) > 0) {
 const taperAmt = b.taper_amount ?? 0;
 const rnrbBefore = b.rnrb_before_taper ?? rnrbAvailable;
 if (taperAmt > 0) {
 // Show taper breakdown. The taper reduces the ALLOWANCE; the RNRB is the lower of the
 // adjusted allowance and the residence value (PA-IHT-RNRB-TAPER-CALC-001).
 const allowance = b.tnrb_detail?.combined_rnrb_max ?? rnrbBefore;
 rows.push({ label: 'Residence allowance (before taper)', value: fmt(allowance) });
 rows.push({ label: 'Less: Taper reduction (estate over £2m)', value: fmtSigned(taperAmt), indent: true, note: 'The allowance is reduced by £1 for every £2 the estate exceeds £2,000,000.' });
 rows.push({ label: 'Adjusted allowance', value: fmt(Math.max(0, allowance - taperAmt)), indent: true });
 rows.push({ label: 'Residence value passing to direct descendants (capped at the allowance)', value: fmt(rnrbBefore), indent: true });
 if ((b.downsizing_addition ?? 0) > 0) {
 rows.push({ label: ' of which: downsizing addition', value: fmt(b.downsizing_addition!), indent: true });
 }
 rows.push({ label: 'Residence Nil Rate Band (lower of the two)', value: rnrbAvailable > 0 ? fmt(rnrbAvailable) : '£0', indent: true, bold: true });
 } else {
 rows.push({ label: 'Residence Nil Rate Band', value: fmt(rnrbAvailable) });
 if ((b.downsizing_addition ?? 0) > 0) {
 rows.push({ label: ' of which: downsizing addition', value: fmt(b.downsizing_addition!), indent: true });
 }
 }
 }
 // Issue 8: Always derive total allowances
 const derivedTotalAllowance = nrbAvailable + rnrbAvailable;
 if (derivedTotalAllowance > 0) rows.push({ label: 'Total allowances', value: fmt(derivedTotalAllowance), bold: true, separator: true });

 if (b.taxable_value != null) rows.push({ label: 'Taxable estate', value: fmt(b.taxable_value), bold: true });
 if (b.gift_no_tax_note) rows.push({ label: b.gift_no_tax_note, value: '', indent: true, note: undefined });

 // Taper relief
 if ((b.taper_relief_total ?? 0) > 0) {
 rows.push({ label: 'Less: Gift taper relief (gifts made 3–7 years before death)', value: fmtSigned(b.taper_relief_total!), indent: true, positive: true, note: 'Taper relief reduces the tax rate on gifts made more than 3 years before death.' });
 }

 return (
 <table className="w-full mb-3">
 <tbody>
 {rows.map((r, i) => (
 <React.Fragment key={i}>
 {r.separator && i > 0 && (
 <tr><td colSpan={2}><div className="border-t border-[#E8E4DB] my-1" /></td></tr>
 )}
 <tr className={r.bold ? 'bg-[#FAFAF8]' : ''}>
 <td className={`py-0.5 pr-2 text-[#3F3D39] ${r.indent ? 'pl-4' : ''} ${r.bold ? 'font-semibold text-[#3F3D39]' : ''}`}>
 {r.label}
 {r.note && <span className="block text-[10px] text-[#AFA99F] font-normal leading-tight">{r.note}</span>}
 </td>
 <td className={`py-0.5 text-right font-mono whitespace-nowrap ${r.positive ? 'text-[#3730A3]' : r.bold ? 'text-[#1A1A1A] font-bold' : 'text-[#3F3D39]'}`}>
 {r.value}
 </td>
 </tr>
 </React.Fragment>
 ))}
 {/* Tax line */}
 {ihtCalc.total_tax_due != null && (
 <>
 <tr><td colSpan={2}><div className="border-t-2 border-[#E8E4DB] my-1" /></td></tr>
 {b.gift_tax_before_taper != null && b.gift_tax_before_taper > 0 ? (
 <>
 {/* Section 1: Lifetime Gift Tax */}
 <tr className="bg-[#EEF0FA]">
 <td className="py-1 pr-2 font-semibold text-[#3730A3]">
 Lifetime gift tax at 40%
 <span className="block text-[10px] text-[#3730A3] font-normal leading-tight">Tax on gifts exceeding available NRB</span>
 </td>
 <td className="py-1 text-right font-mono font-bold text-[#3730A3]">
 {b.gift_tax_before_taper.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
 </td>
 </tr>
 {(b.taper_relief_total ?? 0) > 0 && (
 <tr className="bg-[#EEF0FA]">
 <td className="py-0.5 pr-2 pl-4 text-[#1A1A1A] text-xs">
 Less taper relief
 <span className="block text-[10px] text-[#3730A3] font-normal leading-tight">Reduces gift tax only (not estate tax)</span>
 </td>
 <td className="py-0.5 text-right font-mono text-[#3730A3]">
 −{(b.taper_relief_total ?? 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
 </td>
 </tr>
 )}
 <tr className="bg-[#EEF0FA] border-b border-[#C7CBEA]">
 <td className="py-1 pr-2 pl-4 font-bold text-[#3730A3] text-xs">Gift tax payable</td>
 <td className="py-1 text-right font-mono font-bold text-[#3730A3]">
 {(b.gift_tax_after_taper ?? 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
 </td>
 </tr>
 {/* Section 2: Estate Tax */}
 <tr className="bg-[#FAFAF8]">
 <td className="py-1 pr-2 font-semibold text-[#1A1A1A]">
 {`Estate tax at ${((b.iht_rate_applied ?? b.tax_rate) * 100).toFixed(0)}%`}
 {b.charitable_rate_applies && <span className="ml-1 text-[10px] text-[#1A1A1A] font-normal">(reduced rate)</span>}
 <span className="block text-[10px] text-[#AFA99F] font-normal leading-tight">Tax on the chargeable estate</span>
 </td>
 <td className="py-1 text-right font-mono font-bold text-[#3F3D39]">
 {(b.estate_tax ?? 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
 </td>
 </tr>
 {/* Section 3: Total IHT */}
 <tr><td colSpan={2}><div className="border-t-2 border-[#AFA99F] my-0.5" /></td></tr>
 <tr className="bg-[#FAFAF8]">
 <td className="py-1.5 pr-2 font-bold text-[#1A1A1A] text-sm">
 Total inheritance tax due
 </td>
 <td className={`py-1.5 text-right font-mono font-black text-base ${ihtCalc.total_tax_due === 0 ? 'text-[#3730A3]' : 'text-[#1A1A1A]'}`}>
 {ihtCalc.total_tax_due === 0 ? 'Nil' : ihtCalc.total_tax_due.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
 </td>
 </tr>
 </>
 ) : (
 <tr className="bg-[#FAFAF8]">
 <td className="py-1 pr-2 font-bold text-[#1A1A1A]">
 {`IHT at ${((b.iht_rate_applied ?? b.tax_rate) * 100).toFixed(0)}%`}
 {b.charitable_rate_applies && <span className="ml-1 text-[10px] text-[#1A1A1A] font-normal">(reduced rate — ≥10% to charity)</span>}
 </td>
 <td className={`py-1 text-right font-mono font-black text-base ${ihtCalc.total_tax_due === 0 ? 'text-[#3730A3]' : 'text-[#1A1A1A]'}`}>
 {ihtCalc.total_tax_due === 0 ? 'Nil' : ihtCalc.total_tax_due.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
 </td>
 </tr>
 )}
 </>
 )}
 </tbody>
 </table>
 );
 })()}

 {/* (Zero-tax explanation is now rendered in the consolidated block above) */}

 {/* Property instalment estimate */}
 {mode === 'results' && ihtCalc.total_tax_due != null && ihtCalc.total_tax_due > 0 && (() => {
 const propVal = availableProperties.reduce((sum, p) => sum + (p.estimated_value ?? 0), 0);
 const grossVal = ihtCalc.breakdown.gross_value ?? 0;
 if (grossVal <= 0 || propVal / grossVal <= 0.5) return null;
 const annualInstalment = Math.round(ihtCalc.total_tax_due / 10);
 const fmtI = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 return (
 <div className="bg-[#EEF0FA] border border-[#C7CBEA] rounded p-2.5 text-xs text-[#3730A3] mb-3">
 <p className="font-bold mb-1">Inheritance tax arising from property may be payable in 10 yearly instalments.</p>
 <p>Estimated annual instalment: <span className="font-bold font-mono">{fmtI(annualInstalment)}</span> (+ interest on outstanding balance)</p>
 </div>
 );
 })()}

 {/* Calculation method panel */}
 {(() => {
 const b = ihtCalc.breakdown;
 const steps: string[] = [];
 steps.push('Net estate calculated after debts');
 if ((b.apr_relief_total ?? 0) > 0 || (b.bpr_relief_total ?? 0) > 0) steps.push('APR / BPR relief applied to qualifying assets');
 if ((b.spouse_exemption_total ?? 0) > 0) steps.push('Spouse / civil partner exemption applied');
 if ((b.gifts_chargeable_total ?? 0) > 0) steps.push('Chargeable lifetime gifts calculated; NRB applied to gifts first');
 if ((b.gift_tax_before_taper ?? 0) > 0) steps.push('Gift tax calculated with taper relief');
 steps.push(`Remaining NRB applied to estate${b.tnrb_detail?.nrb_transferred_percent ? ' (includes transferred NRB)' : ''}`);
 if ((b.rnrb_available ?? 0) > 0 || (b.rnrb_before_taper ?? 0) > 0) steps.push(`Residence Nil Rate Band applied${b.tnrb_detail?.rnrb_transferred_percent ? ' (includes transferred RNRB)' : ''}`);
 if ((b.iht_rate_applied ?? 0.40) === 0.36) {
 steps.push('Estate taxed at 36% (charitable rate)');
 } else {
 steps.push('Estate taxed at 40%');
 }
 if ((b.gift_tax_after_taper ?? 0) > 0) steps.push('Total IHT = gift tax + estate tax');
 return (
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded p-2.5 text-[10px] text-[#6B6762] mb-2">
 <span className="font-semibold text-[#3F3D39] block mb-1">Calculation method used</span>
 <ol className="list-decimal list-inside space-y-0.5">
 {steps.map((s, i) => (
 <li key={i}>{s}</li>
 ))}
 </ol>
 {(() => {
 const nrbAuthority = getAuthority('NRB', b.date_of_death);
 return nrbAuthority ? (
 <RegulationReference
 title={nrbAuthority.label}
 legislationReference={nrbAuthority.legislation}
 hmrcManualReference={nrbAuthority.manual}
 asAtDate={b.date_of_death}
 />
 ) : null;
 })()}
 </div>
 );
 })()}

 {/* Audit Trail —"Show calculation steps" */}
 {ihtCalc.audit_trail && ihtCalc.audit_trail.length > 0 && (() => {
 const fmtA = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 return (
 <div className="mb-3">
 <button
 onClick={() => setAuditOpen(!auditOpen)}
 className="flex items-center gap-1 text-[10px] font-semibold text-[#3730A3] hover:text-[#2E2780] uppercase tracking-wide"
 >
 {auditOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
 {auditOpen ? 'Hide calculation steps' : 'Show calculation steps'}
 </button>
 {auditOpen && (
 <div className="mt-2 bg-[#EEF0FA]/50 border border-[#C7CBEA] rounded p-2.5 text-xs">
 <table className="w-full">
 <thead>
 <tr className="text-[10px] text-[#3730A3] uppercase tracking-wider">
 <th className="text-left pb-1 font-semibold">Step</th>
 <th className="text-right pb-1 font-semibold">Value</th>
 </tr>
 </thead>
 <tbody>
 {ihtCalc.audit_trail!.map((s, i) => {
 const isTotal = s.step === 'Total IHT due';
 const isDeduction = s.value !== null && s.value < 0;
 return (
 <tr key={i} className={isTotal ? 'border-t-2 border-[#C7CBEA] bg-[#EEF0FA]/50' : ''}>
 <td className={`py-0.5 pr-2 ${isTotal ? 'font-bold text-[#1A1A1A] pt-1.5' : 'text-[#3F3D39]'}`}>
 {s.step}
 <span className="block text-[10px] text-[#AFA99F] font-normal leading-tight">
 {s.description}
 {s.formula && <span className="ml-1 text-[#3730A3]">({s.formula})</span>}
 </span>
 </td>
 <td className={`py-0.5 text-right font-mono whitespace-nowrap ${
 isTotal ? 'font-black text-base pt-1.5' :
 isDeduction ? 'text-[#3730A3]' : 'text-[#3F3D39]'
 } ${isTotal && s.value === 0 ? 'text-[#3730A3]' : isTotal ? 'text-[#1A1A1A]' : ''}`}>
 {s.value !== null ? fmtA(s.value) : '—'}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </div>
 );
 })()}

 {/* TNRB / TRNRB detail */}
 {mode === 'results' && ihtCalc.breakdown.tnrb_detail && (ihtCalc.breakdown.tnrb_detail.nrb_transferred_percent > 0 || ihtCalc.breakdown.tnrb_detail.rnrb_transferred_percent > 0) && (() => {
 const td = ihtCalc.breakdown.tnrb_detail;
 const fmtD = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 return (
 <div className="bg-[#EEF0FA] border border-[#C7CBEA] rounded p-2.5 text-xs text-[#2E2780] mb-3">
 <p className="font-bold mb-1.5">Transferable Allowances from Predeceased Spouse</p>
 <table className="w-full text-xs">
 <tbody>
 {td.nrb_transferred_percent > 0 && (
 <>
 <tr><td className="py-0.5 pr-2 text-[#3730A3]">Base NRB</td><td className="py-0.5 text-right font-mono">{fmtD(td.nrb_base)}</td></tr>
 <tr><td className="py-0.5 pr-2 text-[#3730A3]">Transferred NRB ({td.nrb_transferred_percent}% unused)</td><td className="py-0.5 text-right font-mono text-[#3730A3]">+{fmtD(td.nrb_transferred_amount)}</td></tr>
 <tr className="font-semibold"><td className="py-0.5 pr-2">Combined NRB</td><td className="py-0.5 text-right font-mono">{fmtD(td.combined_nrb)}</td></tr>
 </>
 )}
 {td.rnrb_transferred_percent > 0 && (
 <>
 <tr><td colSpan={2}><div className="border-t border-[#C7CBEA] my-1" /></td></tr>
 <tr><td className="py-0.5 pr-2 text-[#3730A3]">Base RNRB</td><td className="py-0.5 text-right font-mono">{fmtD(td.rnrb_base)}</td></tr>
 <tr><td className="py-0.5 pr-2 text-[#3730A3]">Transferred RNRB ({td.rnrb_transferred_percent}% unused)</td><td className="py-0.5 text-right font-mono text-[#3730A3]">+{fmtD(td.rnrb_transferred_amount)}</td></tr>
 <tr className="font-semibold"><td className="py-0.5 pr-2">Combined RNRB (max)</td><td className="py-0.5 text-right font-mono">{fmtD(td.combined_rnrb_max)}</td></tr>
 {td.rnrb_taper_applied && (
 <tr><td className="py-0.5 pr-2 text-[#7A5A00]">RNRB taper (estate &gt; £2m)</td><td className="py-0.5 text-right font-mono text-[#7A5A00]">−{fmtD(td.rnrb_taper_reduction)}</td></tr>
 )}
 <tr className="font-semibold"><td className="py-0.5 pr-2">Final RNRB allowed</td><td className="py-0.5 text-right font-mono">{fmtD(td.final_rnrb ?? 0)}</td></tr>
 </>
 )}
 </tbody>
 </table>
 {(() => {
 // NRB: its own confidence. RNRB: the engine's TRNRB claim flag (claimed % is user-provided) — never the NRB confidence (D06).
 const nrbUnverified = td.nrb_transferred_percent > 0 && (ihtCalc.breakdown.tnrb_confidence === 'possible' || !ihtCalc.breakdown.tnrb_confidence);
 const rnrbUnverified = (ihtCalc.confidence?.flags ?? []).some(f => f.field === 'trnrb_claim' && f.level === 'assumed');
 if (!nrbUnverified && !rnrbUnverified) return null;
 const which = nrbUnverified && rnrbUnverified ? 'The transferred NRB and RNRB percentages are' : `The transferred ${nrbUnverified ? 'NRB' : 'RNRB'} percentage is`;
 return <p className="text-[10px] text-[#7A5A00] mt-1.5 italic">{which} unverified — requires documentary confirmation before filing.</p>;
 })()}
 </div>
 );
 })()}

 {/* APR/BPR combined cap notice (post-6 April 2026) */}
 {mode === 'results' && ihtCalc.breakdown.reliefs?.apr_bpr_combined_cap_applied && (() => {
 const r = ihtCalc.breakdown.reliefs;
 const fmtR = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 return (
 <div className="bg-[#FAF3E0] border border-[#E8D89A] rounded p-2.5 text-xs text-[#5A4200] mb-3">
 <p className="font-bold mb-1">APR/BPR Combined Allowance Cap Applied</p>
 <p className="text-[10px] leading-relaxed">
 For deaths on or after 6 April 2026, the first £1,000,000 of qualifying APR/BPR property receives 100% relief.
 Amounts above this receive 50% relief only.
 </p>
 <p className="mt-1 font-mono text-[10px]">
 Total qualifying: {fmtR(r.apr_bpr_combined_qualifying ?? 0)} |
 Additional tax from cap: {fmtR(r.apr_bpr_combined_cap_reduction ?? 0)}
 </p>
 </div>
 );
 })()}
 {mode === 'results' && ihtCalc.breakdown.date_of_death_regime === 'pre_2026' && ((ihtCalc.breakdown.bpr_relief_total ?? 0) > 0 || (ihtCalc.breakdown.apr_relief_total ?? 0) > 0) && (
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded p-2 text-[10px] text-[#AFA99F] mb-3 italic">
 Post-6 April 2026 APR/BPR reforms do not apply to this estate.
 </div>
 )}

 {/* Reliefs Applied summary */}
 {mode === 'results' && ihtCalc.breakdown.reliefs && (() => {
 const r = ihtCalc.breakdown.reliefs;
 const fmtS = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
 const hasAnyRelief = r.spouse_exemption.amount > 0 || r.charity_exemption.amount > 0 || r.bpr.total > 0 || r.apr.total > 0 || (r.nrb.available ?? 0) > 0 || (r.rnrb.available ?? 0) > 0 || r.taper_relief.total > 0 || r.downsizing.amount > 0;
 if (!hasAnyRelief) return null;
 return (
 <div className="bg-white border border-[#E8E4DB] rounded p-2.5 text-xs text-[#1A1A1A] mb-3">
 <p className="font-bold mb-1.5">Reliefs Applied</p>
 <table className="w-full text-xs">
 <tbody>
 {r.spouse_exemption.amount > 0 && <tr><td className="py-0.5 pr-2">Spouse / civil partner exemption</td><td className="py-0.5 text-right font-mono">{fmtS(r.spouse_exemption.amount)}</td></tr>}
 {r.charity_exemption.amount > 0 && <tr><td className="py-0.5 pr-2">Charitable exemption{r.charity_exemption.triggers_36_rate ? ' (triggers 36% rate)' : ''}</td><td className="py-0.5 text-right font-mono">{fmtS(r.charity_exemption.amount)}</td></tr>}
 {r.apr.total > 0 && <tr><td className="py-0.5 pr-2">Agricultural Property Relief</td><td className="py-0.5 text-right font-mono">{fmtS(r.apr.total)}</td></tr>}
 {r.bpr.total > 0 && <tr><td className="py-0.5 pr-2">Business Property Relief</td><td className="py-0.5 text-right font-mono">{fmtS(r.bpr.total)}</td></tr>}
 {(r.nrb.available ?? 0) > 0 && <tr><td className="py-0.5 pr-2">Nil Rate Band{r.tnrb.transferred_percent > 0 ? ` (inc. ${r.tnrb.transferred_percent}% transferred)` : ''}</td><td className="py-0.5 text-right font-mono">{fmtS(r.nrb.available ?? 0)}</td></tr>}
 {(r.rnrb.available ?? 0) > 0 && <tr><td className="py-0.5 pr-2">Residence Nil Rate Band{r.trnrb.transferred_percent > 0 ? ` (inc. ${r.trnrb.transferred_percent}% transferred)` : ''}{r.trnrb.taper_applied ? ' (tapered)' : ''}</td><td className="py-0.5 text-right font-mono">{fmtS(r.rnrb.available ?? 0)}</td></tr>}
 {r.taper_relief.total > 0 && <tr><td className="py-0.5 pr-2">Taper relief on gift tax</td><td className="py-0.5 text-right font-mono">{fmtS(r.taper_relief.total)}</td></tr>}
 {r.downsizing.amount > 0 && <tr><td className="py-0.5 pr-2">Downsizing addition</td><td className="py-0.5 text-right font-mono">{fmtS(r.downsizing.amount)}</td></tr>}
 </tbody>
 </table>
 </div>
 );
 })()}

 {/* Liability notes */}
 {mode === 'results' && ihtCalc.breakdown.liability_notes && ihtCalc.breakdown.liability_notes.length > 0 && (
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded p-2 text-[10px] text-[#6B6762] mb-3">
 {ihtCalc.breakdown.liability_notes.map((note, i) => (
 <p key={i} className="mb-0.5">{note}</p>
 ))}
 </div>
 )}

 {/* Gifts out of income (s21) — figures and their caveats together.
    Rendered only when an s21 assessment actually ran. Its caveat flags are
    filtered out of the generic block below so they appear here, not twice. */}
                {mode === 'results' && (() => {
                    const s21Exempt = ihtCalc.breakdown.gifts_income_exempt_total ?? 0;
                    const s21Overflow = ihtCalc.breakdown.gifts_income_overflow_total ?? 0;
                    const aeApplied = ihtCalc.breakdown.gifts_ae_applied_total ?? 0;
                    const s21Flags = (ihtCalc.confidence?.flags ?? []).filter(f => f.field === 'gift_income_exemption');
                    const s21Ran = s21Exempt > 0 || s21Overflow > 0 || s21Flags.length > 0;
                    if (!(s21Ran || aeApplied > 0)) return null;
                    const gbp = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
                    const assessed = s21Exempt + s21Overflow;
                    return (
                        <div className="bg-white border border-[#E8E4DB] rounded p-3 mb-3 text-xs">
                            <p className="font-bold text-[#1A1A1A] mb-2">Gift exemptions</p>
                            <div className="space-y-1">
                                {s21Ran && (
                                    <>
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-[#3F3D39]">Regular gifts assessed (s21)</span>
                                            <span className="font-medium text-[#1A1A1A]">{gbp(assessed)}</span>
                                        </div>
                                        <div className="flex justify-between items-baseline pl-3">
                                            <span className="text-[#6B6762]">Exempt as normal expenditure out of income</span>
                                            <span className="font-medium text-[#6B6762]">{gbp(s21Exempt)}</span>
                                        </div>
                                        {s21Overflow > 0 && (
                                            <div className="flex justify-between items-baseline pl-3">
                                                <span className="text-[#6B6762]">Reverted to potentially exempt transfer treatment</span>
                                                <span className="font-medium text-[#6B6762]">{gbp(s21Overflow)}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                {aeApplied > 0 && (
                                    <div className="flex justify-between items-baseline">
                                        <span className="text-[#3F3D39]">Annual exemption applied (£3,000 / year)</span>
                                        <span className="font-medium text-[#1A1A1A]">{gbp(aeApplied)}</span>
                                    </div>
                                )}
                            </div>
                            {aeApplied > 0 && (
                                <p className="text-[10px] text-[#6B6762] mt-2 leading-relaxed">
                                    The annual exemption is applied automatically from the gift dates and amounts — there is no figure to enter. It covers the overflow above and any one-off gifts, but never the s21-exempt portion.
                                </p>
                            )}
                            {s21Ran && (
                                <p className="text-[10px] text-[#6B6762] mt-2 leading-relaxed">
                                    These figures depend on the income and expenditure records for each year; the burden of proof for the s21 exemption rests with the personal representatives. This breakdown maps to IHT403 Schedule 2.
                                </p>
                            )}
                            {s21Flags.length > 0 && (
                                <ul className="space-y-1 mt-2">
                                    {s21Flags.map((f, i) => (
                                        <li key={i} className="flex items-start gap-1 text-[10px]">
                                            <span className={f.level === 'missing' ? 'text-[#1A1A1A]' : f.level === 'assumed' ? 'text-[#7A5A00]' : 'text-[#6B6762]'}>
                                                {f.level === 'missing' ? '✕' : '⚠'}
                                            </span>
                                            <span className="text-[#3F3D39]">{f.note}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })()}

                {/* Confidence flags (s21 caveats are surfaced in the s21 block above) */}
 {mode === 'results' && ihtCalc.confidence && ihtCalc.confidence.flags.filter(f => f.field !== 'gift_income_exemption').length > 0 && (
 <div className="bg-[#FAF3E0] border border-[#E8D89A] rounded p-2.5 text-xs text-[#5A4200] mb-3">
 <p className="font-bold mb-1">
 {ihtCalc.confidence.overall === 'incomplete' ? 'Incomplete inputs' : 'Based on user-entered assumptions'}
 </p>
 <ul className="space-y-0.5 text-[10px]">
 {ihtCalc.confidence.flags.filter(f => f.field !== 'gift_income_exemption').map((f, i) => (
 <li key={i} className="flex items-start gap-1">
 <span className={f.level === 'missing' ? 'text-[#1A1A1A]' : 'text-[#7A5A00]'}>
 {f.level === 'missing' ? '✕' : '⚠'}
 </span>
 {f.note}
 </li>
 ))}
 </ul>
 <p className="text-[10px] text-[#7A5A00] mt-1 italic">Requires documentary confirmation before filing.</p>
 </div>
 )}

 {/* Provenance footer */}
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded p-2 text-[10px] text-[#6B6762] space-y-1.5">
 <div>
 {ihtCalc.breakdown.net_value_source === 'calculated' || ihtCalc.breakdown.gross_value_source === 'calculated'
 ? 'Figures calculated from the assets entered in this review.'
 : 'Figures based on values entered manually.'}
 </div>
 {ihtCalc.breakdown.included_asset_classes && ihtCalc.breakdown.included_asset_classes.length > 0 && (
 <div>
 <span className="font-semibold text-[#3F3D39] block mb-0.5">Included in the estimate</span>
 <ul className="space-y-0.5 pl-1">
 {ihtCalc.breakdown.included_asset_classes.map((c, i) => (
 <li key={i} className="flex items-start gap-1">
 <span className="text-[#AFA99F]">•</span>
 {c === 'properties' ? 'Property' :
 c === 'bank_accounts' ? 'Bank accounts' :
 c === 'investments' ? 'Investments' :
 c === 'business_assets_bpr' ? 'Business assets (subject to Business Property Relief)' :
 c === 'agricultural_assets_apr' ? 'Agricultural assets (subject to Agricultural Property Relief)' :
 c.replace(/_/g, ' ')}
 </li>
 ))}
 </ul>
 </div>
 )}
 <div className="text-[#7A5A00]">
 <span className="font-semibold block mb-0.5">Usually outside the estate</span>
 <ul className="space-y-0.5 pl-1">
 {(ihtCalc.breakdown.excluded_asset_classes ?? []).map((c, i) => (
 <li key={`exc-${i}`} className="flex items-start gap-1">
 <span className="text-[#7A5A00]">•</span>
 {c === 'pensions' ? 'Pensions (typically paid directly to beneficiaries and usually outside the estate for IHT purposes)' :
 c.replace(/_/g, ' ')}
 </li>
 ))}
 <li className="flex items-start gap-1">
 <span className="text-[#7A5A00]">•</span>
 Pension death benefits (paid directly to nominated beneficiaries)
 </li>
 <li className="flex items-start gap-1">
 <span className="text-[#7A5A00]">•</span>
 Death-in-service benefits (employer scheme payouts to nominated beneficiaries)
 </li>
 <li className="flex items-start gap-1">
 <span className="text-[#7A5A00]">•</span>
 Trust assets (assets held in trust are generally not part of the deceased's estate)
 </li>
 </ul>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};
